import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Star, Heart, ArrowRight, Sparkles,
  Hash, CaseUpper, Pencil, Calculator, BookOpen, Palette,
  Eye, PenLine, CheckCircle2,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Marquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";
import { CheckoutModal } from "@/components/CheckoutModal";
import { Seo } from "@/components/Seo";
import { API, imgUrl } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1501686637-b7aa9c48a882?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHRvZGRsZXIlMjBwbGF5aW5nJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzkwNTkwNzY1fDA&ixlib=rb-4.1.0&q=80&w=1080";
const HERO_IMG_2 =
  "https://images.unsplash.com/photo-1631032024590-140cc8dd4b32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxjb2xvcmZ1bCUyMGNoaWxkcmVuJTIwcGxheWluZyUyMHNoYXBlc3xlbnwwfHx8fDE3OTA1OTA3NzZ8MA&ixlib=rb-4.1.0&q=80&w=640";

const HERO_LINES = [
  { words: [{ t: "Fun Preschool", c: "#1E293B" }] },
  { words: [{ t: "Learning", c: "#3B82F6" }] },
  { words: [{ t: "Made", c: "#1E293B" }, { t: "Simple.", c: "#EF4444" }] },
];

const SKILL_WORDS = [
  { t: "Trace", c: "#EF4444" },
  { t: "Count", c: "#3B82F6" },
  { t: "Color", c: "#F59E0B" },
  { t: "Practice", c: "#22C55E" },
  { t: "Learn", c: "#A855F7" },
];

const CATEGORY_ICONS = {
  numbers: Hash,
  alphabet: CaseUpper,
  tracing: Pencil,
  counting: Calculator,
  workbooks: BookOpen,
  coloring: Palette,
  math: Calculator,
  shapes: Sparkles,
  colors: Palette,
  phonics: BookOpen,
  bundles: Star,
};

const PLAY_CARDS = [
  { Icon: Pencil, color: "#EF4444", label: "Trace" },
  { Icon: Hash, color: "#3B82F6", label: "Count" },
  { Icon: Palette, color: "#F59E0B", label: "Color" },
  { Icon: Eye, color: "#22C55E", label: "Recognize" },
  { Icon: PenLine, color: "#A855F7", label: "Practice" },
  { Icon: Star, color: "#EC4899", label: "Learn" },
];

const TRUST_CHECKS = [
  "Printable PDF",
  "Designed for Ages 3-6",
  "Fun Learning Activities",
  "Instant Digital Access",
];

const HOME_FAQ = [
  { q: "What age group are these workbooks for?", a: "Our current workbooks are designed primarily for preschool children ages 3-6." },
  { q: "What format do I receive?", a: "You receive a digital PDF workbook that can be downloaded and printed." },
  { q: "Can I print the workbook at home?", a: "Yes. The workbooks are designed for convenient home printing." },
  { q: "Are these physical books?", a: "No. These are digital printable PDF workbooks." },
  { q: "How do I receive my purchase?", a: "After successful payment, access is provided through the digital product checkout." },
  { q: "What subjects are currently available?", a: "Current workbooks include Numbers 1-20 and Alphabet A-Z. More preschool learning products can be added over time." },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ delay, type: "spring", stiffness: 60, damping: 16 }}
    >
      {children}
    </motion.span>
  </span>
);

const FloatShape = ({ className, delay = 0, children }) => (
  <motion.div
    className={`pointer-events-none absolute ${className}`}
    animate={{ y: [0, -14, 0], rotate: [0, 6, 0] }}
    transition={{ duration: 5, delay, repeat: Infinity, ease: "easeInOut" }}
  >
    {children}
  </motion.div>
);

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const img2Y = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => {
    axios.get(`${API}/products?featured=true`).then((r) => setFeatured(r.data.products)).catch(() => {});
    axios.get(`${API}/categories`).then((r) => setCategories(r.data.categories.filter((c) => c.count > 0))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Divine Kids Learning | Printable Preschool Workbooks & Worksheets"
        description="Colorful printable preschool workbooks and learning activities for children ages 3-6. Practice numbers, alphabet, tracing, counting, coloring and early learning through play."
      />

      <section ref={heroRef} className="relative mx-auto max-w-6xl px-6 pb-20 pt-12 sm:pt-16">
        <FloatShape className="left-2 top-10 text-brand-yellow" delay={0.4}>
          <Star className="h-10 w-10 fill-current" />
        </FloatShape>
        <FloatShape className="right-6 top-24 text-brand-purple" delay={1.2}>
          <Sparkles className="h-9 w-9" />
        </FloatShape>
        <FloatShape className="bottom-16 left-6 text-brand-green" delay={0.8}>
          <Heart className="h-8 w-8 fill-current" />
        </FloatShape>

        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <motion.span
              data-testid="hero-badge"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-block rounded-full border-2 border-dashed border-sky-300 bg-white px-5 py-2 font-display text-sm font-bold text-sky-600"
            >
              Printable Workbooks for Ages 3-6
            </motion.span>
            <h1 className="mt-6 font-display text-4xl font-black leading-[1.08] tracking-tight sm:text-6xl lg:text-7xl">
              {HERO_LINES.map((line, i) => (
                <MaskedLine key={i} delay={0.35 + i * 0.15}>
                  {line.words.map((w, j) => (
                    <span key={j} style={{ color: w.c }} className="mr-3 inline-block last:mr-0">
                      {w.t}
                    </span>
                  ))}
                </MaskedLine>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="mt-5 max-w-md text-lg font-semibold leading-relaxed text-slate-500"
            >
              Colorful printable workbooks and activities for little learners ages 3-6.
            </motion.p>
            <motion.p
              data-testid="hero-skill-words"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              className="mt-4 font-display text-lg font-bold"
            >
              {SKILL_WORDS.map((w, i) => (
                <span key={w.t}>
                  <span style={{ color: w.c }}>{w.t}</span>
                  {i < SKILL_WORDS.length - 1 && <span className="mx-2 text-slate-300">•</span>}
                </span>
              ))}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link
                to="/workbooks"
                data-testid="hero-explore-workbooks"
                className="inline-flex items-center gap-3 rounded-full bg-brand-red px-8 py-4 font-display text-lg font-bold text-white shadow-[0_6px_0_0_#B91C1C] transition-transform active:translate-y-1 active:shadow-none"
              >
                Explore Workbooks <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/product/numbers-1-20"
                data-testid="hero-view-numbers"
                className="inline-flex items-center gap-3 rounded-full border-3 border-brand-blue bg-white px-8 py-4 font-display text-lg font-bold text-brand-blue shadow-[0_6px_0_0_#BFDBFE] transition-transform active:translate-y-1 active:shadow-none"
              >
                View Numbers 1-20
              </Link>
            </motion.div>
          </div>

          <div className="relative">
            <motion.div
              style={{ y: imgY }}
              initial={{ opacity: 0, scale: 0.95, rotate: 3 }}
              animate={{ opacity: 1, scale: 1, rotate: 2 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="relative z-10 overflow-hidden rounded-[2.5rem] border-8 border-white shadow-2xl shadow-blue-900/10"
            >
              <img src={HERO_IMG} alt="Happy preschool child learning through play with colorful blocks" className="h-72 w-full object-cover sm:h-[24rem]" />
            </motion.div>
            <motion.div
              style={{ y: img2Y }}
              initial={{ opacity: 0, scale: 0.9, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: -5 }}
              transition={{ delay: 0.8, duration: 0.7 }}
              className="absolute -bottom-10 -left-4 z-20 w-40 overflow-hidden rounded-[2rem] border-8 border-white shadow-xl shadow-blue-900/10 sm:-left-10 sm:w-52"
            >
              <img src={HERO_IMG_2} alt="Child holding colorful learning shapes" className="h-32 w-full object-cover sm:h-40" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              className="absolute -right-3 top-6 z-20 animate-float rounded-2xl bg-white px-5 py-3 shadow-xl shadow-blue-900/10"
            >
              <p className="font-display text-2xl font-black text-brand-green">₹49</p>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">per workbook</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-6xl px-6 py-20">
        <motion.div
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 max-w-2xl"
        >
          <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-purple">
            What's Inside
          </p>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-800 sm:text-5xl">
            Learning Through Play
          </h2>
          <p className="mt-4 text-lg font-semibold text-slate-500">
            Designed to make early learning fun, simple and engaging for little learners.
          </p>
        </motion.div>
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
          {PLAY_CARDS.map((c, i) => (
            <motion.div
              key={c.label}
              data-testid={`play-card-${c.label.toLowerCase()}`}
              initial={{ y: 24 }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="flex flex-col items-center rounded-[1.75rem] bg-white p-6 text-center shadow-lg shadow-blue-900/5 transition-transform duration-300 hover:-translate-y-2"
            >
              <span
                className="flex h-14 w-14 items-center justify-center rounded-full"
                style={{ backgroundColor: `${c.color}1A` }}
              >
                <c.Icon className="h-7 w-7" style={{ color: c.color }} />
              </span>
              <span className="mt-3 font-display text-lg font-bold text-slate-700">{c.label}</span>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-6">
        <div
          data-testid="trust-strip"
          className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 rounded-[2rem] bg-white px-8 py-6 shadow-lg shadow-blue-900/5"
        >
          {TRUST_CHECKS.map((t) => (
            <span key={t} className="flex items-center gap-2 font-display text-base font-bold text-slate-700">
              <CheckCircle2 className="h-5 w-5 text-brand-green" /> {t}
            </span>
          ))}
        </div>
      </section>

      {categories.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-20">
          <motion.div
            initial={{ y: 30 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-10 max-w-2xl"
          >
            <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-purple">
              Browse by Category
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-800 sm:text-5xl">
              What will your child learn today?
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
            {categories.map((c, i) => {
              const Icon = CATEGORY_ICONS[c.slug] || BookOpen;
              return (
                <motion.div
                  key={c.slug}
                  initial={{ y: 24 }}
                  whileInView={{ y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                >
                  <Link
                    to={`/shop?category=${c.slug}`}
                    data-testid={`category-${c.slug}`}
                    className="flex h-full flex-col items-center rounded-[1.75rem] bg-white p-6 text-center shadow-lg shadow-blue-900/5 transition-transform duration-300 hover:-translate-y-2"
                  >
                    <span
                      className="flex h-14 w-14 items-center justify-center rounded-full"
                      style={{ backgroundColor: `${c.color}1A` }}
                    >
                      <Icon className="h-7 w-7" style={{ color: c.color }} />
                    </span>
                    <span className="mt-3 font-display text-sm font-bold leading-tight text-slate-700">
                      {c.label}
                    </span>
                    <span className="mt-1 text-xs font-bold text-slate-400">
                      {c.count} product{c.count === 1 ? "" : "s"}
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-6 pb-20">
        <motion.div
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 flex flex-wrap items-end justify-between gap-4"
        >
          <div>
            <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-red">
              Featured Products
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-800 sm:text-5xl">
              Loved by little learners
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="view-all-products"
            className="inline-flex items-center gap-2 font-display text-lg font-bold text-brand-blue hover:underline"
          >
            View all products <ArrowRight className="h-5 w-5" />
          </Link>
        </motion.div>
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
          {featured.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} onBuy={setSelected} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-20">
        <div className="grid items-center gap-10 rounded-[2.5rem] bg-gradient-to-br from-amber-50 to-rose-50 p-10 shadow-xl shadow-blue-900/5 sm:p-14 lg:grid-cols-2">
          <motion.img
            initial={{ x: -30 }}
            whileInView={{ x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            src={imgUrl(HERO_IMG_2)}
            alt="Preschool child playing with colorful learning shapes"
            loading="lazy"
            className="h-64 w-full rounded-[2rem] border-8 border-white object-cover shadow-lg sm:h-80"
          />
          <motion.div
            initial={{ x: 30 }}
            whileInView={{ x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-green">
              About Us
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl">
              Learning resources made with love
            </h2>
            <p className="mt-4 text-lg font-semibold leading-relaxed text-slate-600">
              Divine Kids Learning creates fun, colorful and printable educational resources for
              young children. Every worksheet is designed to make early learning joyful for kids —
              and effortless for parents, teachers and homeschoolers.
            </p>
            <Link
              to="/about"
              data-testid="about-read-more"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3.5 font-display text-lg font-bold text-white shadow-[0_5px_0_0_#15803D] transition-transform active:translate-y-1 active:shadow-none"
            >
              Read our story <ArrowRight className="h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-24">
        <motion.div
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-blue">
            Good to Know
          </p>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-800 sm:text-5xl">
            Frequently asked questions
          </h2>
        </motion.div>
        <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="home-faq">
          {HOME_FAQ.map((f, i) => (
            <AccordionItem key={i} value={`home-faq-${i}`} className="rounded-2xl border-0 bg-white px-6 shadow-md shadow-blue-900/5">
              <AccordionTrigger data-testid={`home-faq-question-${i}`} className="text-left font-display text-lg font-bold text-slate-700 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-base font-semibold leading-relaxed text-slate-500">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <CheckoutModal product={selected} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}
